import React, { useState, useMemo } from 'react';
import './Style/AllUsers.css';
import TopNavbar from '../home/TopNavbar';
import Loading from '../home/Loading';
import UseUsersDataBeforeLogin from './UseUsersDataBeforeLogin';

const toIST = (dateString) => {
    if (!dateString) return 'Loading...';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};


const AllUsersBeforeLogin = () => {
    const { allusers, loading } = UseUsersDataBeforeLogin();

    // Search
    const [searchTerm, setSearchTerm] = useState('');

    // Items per page
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Current page
    const [currentPage, setCurrentPage] = useState(1);

    /*
     * Filter users
     *
     * Search works on:
     * Name
     * Class
     * Phone
     * Email
     * Date
     * ID
     */
    const filteredUsers = useMemo(() => {
        if (!allusers) {
            return [];
        }

        const search = searchTerm
            .toLowerCase()
            .trim();

        // No search
        if (!search) {
            return allusers;
        }

        return allusers.filter((user) => {
            return [
                user.name,
                user.class,
                user.phone,
                user.email,
                user.date,
                user.id
            ]
                .filter(
                    value =>
                        value !== null &&
                        value !== undefined
                )
                .some(value =>
                    String(value)
                        .toLowerCase()
                        .includes(search)
                );
        });

    }, [allusers, searchTerm]);


    /*
     * Loading check
     *
     * IMPORTANT:
     * This is AFTER all Hooks.
     */
    if (loading) {
        return <Loading />;
    }


    /*
     * Total pages
     */
    const totalPages =
        itemsPerPage === 'all'
            ? 1
            : Math.ceil(
                filteredUsers.length /
                Number(itemsPerPage)
            );


    /*
     * Starting index
     */
    const startIndex =
        itemsPerPage === 'all'
            ? 0
            : (currentPage - 1) *
            Number(itemsPerPage);


    /*
     * Users displayed on current page
     */
    const displayedUsers =
        itemsPerPage === 'all'
            ? filteredUsers
            : filteredUsers.slice(
                startIndex,
                startIndex + Number(itemsPerPage)
            );


    /*
     * Search handler
     */
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);

        // Return to first page
        setCurrentPage(1);
    };


    /*
     * Items per page handler
     */
    const handleItemsPerPage = (e) => {
        setItemsPerPage(e.target.value);

        // Return to first page
        setCurrentPage(1);
    };


    /*
     * Page change
     */
    const goToPage = (page) => {
        if (
            page >= 1 &&
            page <= totalPages
        ) {
            setCurrentPage(page);
        }
    };


    return (
        <div className="all-user-data-container">

            <div className="user-data-content-wrapper">


                <div className="user-data-card">

                    <h2 style={{ color: 'black' }}>
                        All Users Information
                    </h2>


                    {/* =================================
                        Search + Items Per Page
                    ================================= */}

                    <div className="user-table-controls">

                        {/* Search */}

                        <div className="user-search-box">

                            <input
                                type="text"
                                placeholder="Search name, class, phone, email, date, ID..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />

                            {searchTerm && (
                                <button
                                    type="button"
                                    className="clear-search-btn"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setCurrentPage(1);
                                    }}
                                >
                                    ×
                                </button>
                            )}

                        </div>


                        {/* Items per page */}

                        <div className="items-per-page">

                            <label htmlFor="itemsPerPage">
                                Show:
                            </label>

                            <select
                                id="itemsPerPage"
                                value={itemsPerPage}
                                onChange={handleItemsPerPage}
                            >

                                <option value="10">
                                    10
                                </option>

                                <option value="25">
                                    25
                                </option>

                                <option value="50">
                                    50
                                </option>

                                <option value="100">
                                    100
                                </option>

                                <option value="all">
                                    All
                                </option>

                            </select>

                            <span>
                                items
                            </span>

                        </div>

                    </div>


                    {/* =================================
                        Statistics
                    ================================= */}

                    <div className="user-data-stats">

                        <p>
                            Total Users:{' '}
                            <strong>
                                {allusers
                                    ? allusers.length
                                    : 0}
                            </strong>
                        </p>

                        <p>
                            Search Result:{' '}
                            <strong>
                                {filteredUsers.length}
                            </strong>
                        </p>

                    </div>


                    {/* =================================
                        Table
                    ================================= */}

                    <div className="table-responsive">

                        <table className="user-data-table">

                            <thead>

                                <tr>

                                    <th>#</th>

                                    <th>
                                        Name
                                    </th>

                                    <th>
                                        Class
                                    </th>

                                    <th>
                                        Phone
                                    </th>

                                    <th>
                                        Email
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {displayedUsers.length > 0 ? (

                                    displayedUsers.map(
                                        (user, index) => (

                                            <tr
                                                key={
                                                    user.id ||
                                                    index
                                                }
                                            >

                                                <td>
                                                    {startIndex +
                                                        index +
                                                        1}
                                                </td>

                                                <td>
                                                    {user.name ||
                                                        'N/A'}
                                                </td>

                                                <td>
                                                    {user.class ||
                                                        'N/A'}
                                                </td>

                                                <td>
                                                    {user.phone ||
                                                        'N/A'}
                                                </td>

                                                <td>
                                                    {user.email ||
                                                        'N/A'}
                                                </td>

                                                <td>
                                                    {toIST(user.date) ||
                                                        'N/A'}
                                                </td>

                                            </tr>

                                        )
                                    )

                                ) : (

                                    <tr>

                                        <td
                                            colSpan="6"
                                            className="no-data-message"
                                        >
                                            {searchTerm
                                                ? 'No users found for this search'
                                                : 'No users found'}
                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>


                    {/* =================================
                        Pagination
                    ================================= */}

                    {itemsPerPage !== 'all' &&
                        filteredUsers.length > 0 && (

                            <div className="user-pagination">

                                <button
                                    onClick={() =>
                                        goToPage(
                                            currentPage - 1
                                        )
                                    }
                                    disabled={
                                        currentPage === 1
                                    }
                                >
                                    ← Previous
                                </button>


                                <span>

                                    Page{' '}

                                    <strong>
                                        {currentPage}
                                    </strong>

                                    {' '}of{' '}

                                    <strong>
                                        {totalPages}
                                    </strong>

                                </span>


                                <button
                                    onClick={() =>
                                        goToPage(
                                            currentPage + 1
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                >
                                    Next →
                                </button>

                            </div>

                        )}


                    {/* =================================
                        Bottom Information
                    ================================= */}

                    <div className="user-data-stats">

                        <p>

                            Showing{' '}

                            <strong>
                                {filteredUsers.length === 0
                                    ? 0
                                    : startIndex + 1}
                            </strong>

                            {' '}to{' '}

                            <strong>
                                {Math.min(
                                    startIndex +
                                    displayedUsers.length,
                                    filteredUsers.length
                                )}
                            </strong>

                            {' '}of{' '}

                            <strong>
                                {filteredUsers.length}
                            </strong>

                            {' '}users

                        </p>

                    </div>

                </div>

            </div>

        </div>
    );
};

export default AllUsersBeforeLogin;